import CryptoJS from './sha1.js';
import axios from 'axios';
import store from 'commons/store/Store.js';

// Timeouts only. A generic network failure (no connection, DNS, CORS) reports
// ERR_NETWORK and is covered by `isNetworkError` instead, so keeping it here
// would blur the very distinction these flags exist for.
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

export const requestSuccessInterceptor = (config) => {
  if (
    config.url.indexOf(SPRD.API_PATH) !== -1 ||
    config.url.indexOf('/terms-and-conditions') !== -1
  ) {
    const time = new Date().getTime();
    const sig = CryptoJS.SHA1(
      `${config.method.toUpperCase()} ${config.url} ${time} ${SPRD.API_SECRET}`,
    );

    if (config.params === undefined) {
      config.params = {};
    }
    config.params.apiKey = SPRD.API_KEY;
    config.params.sig = `${sig}`;
    config.params.time = time;

    config.headers['Sprd-Href'] = config.url;
  }
  return config;
};

/**
 * Turns an axios error into a plain object callers can always inspect.
 *
 * On network errors and timeouts axios leaves `error.response` undefined, so
 * rejecting with it made every `error.status` / `error.data` read throw. The
 * returned object keeps the response shape (status, data, headers, config) for
 * backwards compatibility and adds the flags needed to tell the failure modes
 * apart.
 */
export const normalizeApiError = (error) => {
  // A cancelled request is not a transient failure - the caller asked for it.
  // Without this it falls through to the no-response branch, gets flagged
  // `isNetworkError`, and apiClient re-issues a request that was deliberately
  // aborted.
  if (axios.isCancel(error)) {
    return {
      status: 0,
      data: undefined,
      headers: {},
      config: error?.config,
      isNetworkError: false,
      isTimeout: false,
      isCanceled: true,
      code: error?.code,
      message: error?.message,
    };
  }

  const isTimeout = TIMEOUT_CODES.includes(error?.code);

  if (error?.response) {
    return {
      ...error.response,
      isNetworkError: false,
      isTimeout: false,
      isCanceled: false,
      code: error.code,
      message: error.message,
    };
  }

  return {
    status: 0,
    data: undefined,
    headers: {},
    config: error?.config,
    isNetworkError: true,
    isTimeout,
    isCanceled: false,
    code: error?.code,
    message: error?.message,
  };
};

export const responseErrorInterceptor = (error) => {
  if (
    [401, 403].includes(error?.response?.status) &&
    error?.response?.config?.url?.indexOf('/users/current') === -1 &&
    error?.response?.config?.url?.indexOf('/sessions') === -1 &&
    error?.response?.config?.url?.indexOf('/identities') === -1 &&
    !store.state.user.isUserLoggingOut
  ) {
    store.dispatch('user/logout');
  }

  return Promise.reject(normalizeApiError(error));
};

axios.interceptors.request.use(requestSuccessInterceptor);

axios.interceptors.response.use(
  (response) => response,
  responseErrorInterceptor,
);
